import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  RollbackOutlined,
  SaveOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  message,
  Popconfirm,
  Table,
  Tooltip,
  Drawer,
} from "antd";
import { createComponentStore, useStoreState } from "easy-peasy";
import React, { useEffect, useState } from "react";
import {
  ParameterStoreModel,
  ACTION_TYPES,
} from "store/models/parameters/general-parameter";
import { formatDate } from "Utils";
import EditableCell from "../../general/components/editable-cell";
import { AuthCheck, AUTH_ACTIONS } from "authorization";
import Yup from "ValidationModels/localizedYup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import moment from "moment";

export default function MailList({ title, api, parentId, ...props }) {
  const { abilities } = useStoreState((store) => store.auth);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedRow, setSelectedRow] = useState(null);

  const isEditing = (record) => record.id === selectedRow?.id;

  const useParameterStore = createComponentStore(ParameterStoreModel(api));
  const [store, actions] = useParameterStore();

  const { fetchSingle, update, create, remove } = actions;

  useEffect(() => {
    fetchSingle({ id: parentId });
  }, []);

  useEffect(() => {
    if (store.process === ACTION_TYPES.FETCHED_DATA) {
      setTableData(store.record.emails);
    }

    if (store.process === ACTION_TYPES.CREATED_DATA) {
      message.success(`Yeni ${title} oluşturuldu.`);
      fetchSingle({ id: parentId });
    }

    if (store.process === ACTION_TYPES.UPDATED_DATA) {
      message.success(`${title} güncellendi.`);
      fetchSingle({ id: parentId });
    }

    if (store.process === ACTION_TYPES.REMOVED_DATA) {
      message.success(`${title} silindi.`);
      fetchSingle({ id: parentId });
    }
  }, [store.process]);

  const columns = [
    {
      title: "E-Posta",
      dataIndex: "email",
      key: "email",
      width: "45%",
      editable: true,
    },
    {
      title: "Yaratma Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => {
        return formatDate(record.createdAt);
      },
    },
    {
      title: "İşlemler",
      width: "150px",
      dataIndex: "operation",
      render: (text, record) => {
        return isEditing(record) ? (
          <span>
            <Tooltip title="Sakla">
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  store.process === ACTION_TYPES.CREATING_DATA ||
                  store.process === ACTION_TYPES.UPDATING_DATA
                }
                icon={<SaveOutlined />}
                style={{
                  marginRight: 8,
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Emin Misin?"
              onConfirm={() => handleCancel(record.id)}
              icon={<RollbackOutlined />}
              okText="İptal"
              cancelText="Geri"
            >
              <Tooltip title="İptal" placement="bottom">
                <Button type="default" icon={<RollbackOutlined />} style={{}} />
              </Tooltip>
            </Popconfirm>
          </span>
        ) : (
          <span>
            <Tooltip title="Düzenle">
              <Button
                type="primary"
                icon={<EditOutlined />}
                disabled={
                  AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Mail Grupları")
                    ? selectedRow
                      ? true
                      : false
                    : true
                }
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedRow(record);
                }}
                style={{
                  marginRight: 8,
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Emin Misin?"
              onConfirm={() => handleDelete(record)}
              icon={<DeleteOutlined />}
              okText="Kaldır"
              cancelText="Geri"
            >
              <Tooltip title="Kaldır" placement="bottom">
                <Button
                  disabled={
                    AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Mail Grupları")
                      ? selectedRow
                        ? true
                        : false
                      : true
                  }
                  loading={store.process === ACTION_TYPES.REMOVING_DATA}
                  type="danger"
                  icon={<DeleteOutlined />}
                  style={{
                    marginRight: 8,
                  }}
                />
              </Tooltip>
            </Popconfirm>
          </span>
        );
      },
    },
  ];

  const FormValidation = Yup.object({
    email: Yup.string().email().required(),
  });

  const form = useForm({
    mode: "onSubmit",
    resolver: yupResolver(FormValidation),
    defaultValues: {},
  });

  const { handleSubmit, errors, reset } = form;

  const formOnSubmit = async (form) => {
    let data = { ...store.record };
    if (selectedRow.id !== "new") {
      data.emails = data.emails.filter((email) => email.id != selectedRow.id);
    }
    data.emails.push(form);
    await update(data);

    handleCancel();
  };

  const handleTablePagination = (change) => {
    setCurrentPage(change);
  };

  const handleDelete = async (record) => {
    let data = { ...store.record };
    data.emails = data.emails.filter((email) => email.id != record.id);
    await update(data);
    handleCancel();
  };

  const handleCancel = (id) => {
    if (id === "new") {
      setTableData((oldArray) => oldArray.filter((item) => item.id !== id));
    }

    setSelectedRow(null);
  };

  const handleAdd = () => {
    setCurrentPage(1);
    reset();

    const newData = {
      id: "new",
      email: "",
      createdAt: moment(),
    };

    setTableData((oldArray) => [newData, ...oldArray]);
    setSelectedRow(newData);
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        form: form,
      }),
    };
  });

  return (
    <>
      <form onSubmit={handleSubmit(formOnSubmit)}>
        <Table
          rowKey={(row) => row.id}
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          title={() => {
            return (
              <Button
                type="primary"
                shape="circle"
                disabled={
                  AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Mail Grupları")
                    ? selectedRow
                      ? true
                      : false
                    : true
                }
                onClick={() => handleAdd()}
                icon={<PlusOutlined />}
                size={10}
              />
            );
          }}
          bordered
          dataSource={tableData}
          columns={mergedColumns}
          size="small"
          loading={store.process === ACTION_TYPES.FETCHING_DATA}
          rowClassName="editable-row"
          pagination={{
            onChange: handleTablePagination,
            current: currentPage,
            defaultPageSize: 20,
          }}
        />
      </form>
    </>
  );
}
